import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';
import { PositionCustody } from 'src/app/shared/interfaces/position-custody';
import {
  CarbonCreditTypeBadge,
  displayValue,
  isAcxCredit,
  isExternalUrl,
  resolveCarbonCreditTypeBadge,
} from 'src/app/pages/register/characteristic-consult/shared/characteristic-consult-details.utils';
import {
  CharacteristicConsultStatusBadge,
  resolveCharacteristicConsultStatusBadge,
} from 'src/app/pages/register/characteristic-consult/shared/characteristic-consult-status-badge.utils';
import {
  resolvePositionComitenteLabel,
  resolvePositionSerialLabel,
} from '../shared/position-consult.utils';

@Component({
  standalone: false,
  selector: 'app-position-consult-details-drawer',
  templateUrl: './position-consult-details-drawer.component.html',
  styleUrls: ['./position-consult-details-drawer.component.scss'],
})
export class PositionConsultDetailsDrawerComponent implements OnChanges {
  @Input() isOpen = false;
  /** Linha da listagem (join issuance no BE já traz projectId e demais campos). */
  @Input() position: PositionCustody | null = null;

  @Output() closed = new EventEmitter<void>();

  detail: PositionCustody | null = null;
  isLoading = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['position']) {
      this.isLoading = false;
      this.detail = this.isOpen ? this.position : null;
    }
  }

  /** Figma ACX: badge "Depósito" (tipo CARB). */
  get typeBadge(): CarbonCreditTypeBadge {
    return resolveCarbonCreditTypeBadge(this.asCarbonCreditForBadges());
  }

  /** Figma não-ACX: badge "Aprovado" (status). */
  get statusBadge(): CharacteristicConsultStatusBadge {
    return resolveCharacteristicConsultStatusBadge(
      this.detail?.statusCodeDescription ?? this.detail?.statusCode,
    );
  }

  get showAcxBadge(): boolean {
    return this.detail ? isAcxCredit(this.asCarbonCreditForBadges()) : false;
  }

  get showBrcerBadge(): boolean {
    return this.detail?.brcerIndicator === true;
  }

  /** ACX → Depósito (+ ACX/BRCER). Caso contrário → status (Aprovado). */
  get showTypeBadges(): boolean {
    return this.showAcxBadge || this.showBrcerBadge;
  }

  get showStatusBadge(): boolean {
    return !!this.detail && !this.showAcxBadge && this.statusBadge.label !== '-';
  }

  /** Figma: Tipo IF = CARB (não o int 1). */
  get instrumentType(): string {
    return displayValue(
      this.detail?.financialInstrumentTypeCode ?? this.detail?.financialInstrumentCode,
    );
  }

  get issueDate(): string {
    return this.formatDisplayDate(this.detail?.issueDate);
  }

  get comitenteDocument(): string {
    if (!this.detail) {
      return '-';
    }
    return resolvePositionComitenteLabel(this.detail);
  }

  get issuedQuantity(): string {
    return displayValue(this.detail?.creditInitQnty);
  }

  get currentQuantity(): string {
    return displayValue(this.detail?.creditQnty);
  }

  get vintageStart(): string {
    return this.formatDisplayDate(this.detail?.vintageStart);
  }

  get vintageEnd(): string {
    return this.formatDisplayDate(this.detail?.vintageEnd);
  }

  get serialCode(): string {
    return this.detail ? resolvePositionSerialLabel(this.detail) : '-';
  }

  get projectName(): string {
    return displayValue(this.detail?.projectName);
  }

  get projectLink(): string {
    return displayValue(this.detail?.projectLink);
  }

  get certifierName(): string {
    return displayValue(this.detail?.certifierDescription);
  }

  get additionalDescription(): string {
    return displayValue(this.detail?.observationText);
  }

  get hasProjectLink(): boolean {
    return isExternalUrl(this.detail?.projectLink);
  }

  close(): void {
    this.closed.emit();
  }

  private asCarbonCreditForBadges(): CarbonCredit {
    return {
      financialInstrumentTypeCode: this.detail?.financialInstrumentTypeCode ?? undefined,
      statusCode: this.detail?.statusCode ?? undefined,
      certifierDescription: this.detail?.certifierDescription ?? undefined,
      brcerIndicator: this.detail?.brcerIndicator ?? undefined,
    } as CarbonCredit;
  }

  private formatDisplayDate(value?: string | null): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
    if (iso) {
      return `${iso[3]}/${iso[2]}/${iso[1]}`;
    }

    return displayValue(value);
  }
}
